import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Alert, Grid, Skeleton, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import RestartAlt from '@mui/icons-material/RestartAlt';

import { Form, Field } from 'react-final-form';
import { Select, Switches, TextField } from 'mui-rff';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import MultiSelect, {
  REASON_CLEAR,
  REASON_REMOVE_OPTION,
  REASON_SELECT_OPTION,
} from '@components/MultiSelect';
import { Typography } from '@components/styledComponents';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { addContextualMessages } from '../../payment-options.breadcrumbs';
import {
  getLOBDetails,
  getLOBs,
  selectLOBList,
} from '../../store/payment-options-merchants';
import {
  createContextualMessagesMerchantMapping,
  getContextualMessageBannerList,
  selectContextualMessageBannerList,
  selectIsLoadingContextualMessageFormStatus,
} from '../../store/payment-options-contextual-messages';

import SelectedMerchantsTable from '../../components/SelectedMerchantsTable';

export const CASHIER_BANNER_TYPES = [
  'info',
  'warning',
  'success',
  'error',
  'infoV6',
  'warningV6',
];

export default function ContextualMessagesForm() {
  const dispatch = useDispatch();
  const permissions = usePermissions();

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isLoadingSelectedLOBMerchants, setIsLoadingSelectedLOBMerchants] =
    useState(false);
  const [selectedLOBMerchants, setSelectedLOBMerchants] = useState([]);
  const [showMerchantList, setShowMerchantList] = useState(false);
  const [showAddMethodBtn, setShowAddMethodBtn] = useState(false);
  const [addedMerchants, setAddedMerchants] = useState([]);

  const [showTable, setShowTable] = useState(false);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  const selectedMerchants = useRef({ lob: null, merchants: [] });
  const formValue = useRef({
    lob: null,
    merchants: null,
    banner: '',
    bannerType: 'info',
    overridePossible: true,
    message: '',
  });

  const lobList = useSelector(selectLOBList);
  const contextualMessageBannerList = useSelector(
    selectContextualMessageBannerList,
  );
  const isSubmittingForm = useSelector(
    selectIsLoadingContextualMessageFormStatus,
  );

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(addContextualMessages));
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingData(true);
    return Promise.all([
      dispatch(getLOBs()),
      dispatch(getContextualMessageBannerList()),
    ]).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  useEffect(
    () => setShowTable(addedMerchants && addedMerchants.length > 0),
    [addedMerchants],
  );

  const resetStep1 = useCallback(() => {
    selectedMerchants.current = { lob: null, merchants: [] };
    formValue.current = { ...formValue.current, lob: null, merchants: null };
    setShowMerchantList(false);
    setShowAddMethodBtn(false);
    setSelectedLOBMerchants([]);
  }, [selectedMerchants, formValue]);

  const handleAddMerchant = useCallback(() => {
    const newLobId = selectedMerchants.current.lob.lobId;
    const newMerchants = selectedMerchants.current.merchants;

    const existingIdx = addedMerchants.findIndex(
      (merchant) => merchant.lob.lobId === newLobId,
    );
    let updatedMerchants = addedMerchants.concat(selectedMerchants.current);

    if (existingIdx !== -1) {
      updatedMerchants = [...addedMerchants];
      updatedMerchants[existingIdx] = {
        ...selectedMerchants.current,
        merchants: newMerchants,
      };
    }
    setAddedMerchants(updatedMerchants);
    resetStep1();
  }, [selectedMerchants, addedMerchants, resetStep1]);

  const handleRemoveMerchant = useCallback((lobId, setValue) => {
    setAddedMerchants((current) => {
      const filteredCurrent = current.filter(({ lob }) => lob.lobId !== lobId);
      if (!filteredCurrent || filteredCurrent.length === 0) {
        setValue('lob', null);
        setValue('merchants', []);
      }
      return filteredCurrent;
    });
  }, []);

  const handleOnChangeLOB = useCallback(
    (value, reason) => {
      if (reason === REASON_CLEAR || value === null) {
        resetStep1();
        return;
      }

      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowMerchantList(true);
        setIsLoadingSelectedLOBMerchants(true);

        dispatch(getLOBDetails(value.lobId)).then(
          ({ payload: { merchantDetails } }) => {
            setSelectedLOBMerchants(merchantDetails);

            const lobExistsIdx = addedMerchants.findIndex(
              (merchant) => merchant.lob.lobId === value.lobId,
            );

            // reselect existing merchants
            selectedMerchants.current = {
              lob: value,
              merchants:
                lobExistsIdx > -1 ? addedMerchants[lobExistsIdx].merchants : [],
            };
            setIsLoadingSelectedLOBMerchants(false);
          },
        );
      }
    },
    [dispatch, resetStep1, selectedMerchants, addedMerchants],
  );

  const handleOnChangeMerchants = useCallback(
    (values, reason) => {
      if (reason === REASON_CLEAR) {
        setShowAddMethodBtn(false);
      }
      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowAddMethodBtn(values && values.length > 0);
      }
      selectedMerchants.current = {
        ...selectedMerchants.current,
        merchants: values,
      };
    },
    [selectedMerchants],
  );

  const onSubmit = ({
    banner: { id: bannerId },
    message,
    bannerType,
    overridePossible,
  }) => {
    setFormSuccessMsg('');
    setFormErrorMsg('');

    const merchantIds = addedMerchants.flatMap(({ merchants }) =>
      merchants.map(({ merchantId }) => merchantId),
    );

    const payload = {
      merchantIds,
      bannerId: bannerId.toString(),
      message,
      bannerType,
      overridePossible: overridePossible ? 1 : 0,
    };

    dispatch(createContextualMessagesMerchantMapping(payload)).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        setFormSuccessMsg(
          'Contextual messages merchant mapping has been updated successfully',
        );
      } else {
        setFormErrorMsg(data.payload?.message || data?.error.message);
      }
    });
  };

  const validate = (values) => {
    const { banner, message, merchants, lob } = values;
    return {
      ...(!lob && !addedMerchants.length && { lob: 'Add an LOB' }),
      ...(!merchants && { merchants: 'Add a platform' }),
      ...(!banner && { banner: 'Location is required' }),
      ...(!message && { message: 'Message is required' }),
    };
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="New contextual message mapping"
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue.current}
          validate={validate}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          render={({
            handleSubmit,
            pristine,
            submitting,
            form: {
              mutators: { setValue },
            },
          }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <Typography
                className="form-header"
                variant="formHeader">
                Step 1
              </Typography>
              {showTable && (
                <SelectedMerchantsTable
                  merchants={addedMerchants}
                  handleRemoveMerchant={(lobId) =>
                    handleRemoveMerchant(lobId, setValue)
                  }
                />
              )}
              <Grid
                container
                sx={{ marginTop: '0' }}
                spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={3}>
                  <MultiSelect
                    id="lobList"
                    name="lob"
                    items={lobList}
                    value={selectedMerchants.current.lob}
                    isOptionEqualToValue={(option, value) =>
                      option.lobId === value.lobId
                    }
                    multiple={false}
                    label="Select LOB"
                    optionDisplayKey="lobName"
                    onChange={handleOnChangeLOB}
                  />
                </Grid>
                {showMerchantList && (
                  <Grid
                    item
                    xs={9}
                    md={5}>
                    {isLoadingSelectedLOBMerchants ? (
                      <Skeleton
                        variant="rounded"
                        height={56}
                      />
                    ) : (
                      <MultiSelect
                        id="merchantList"
                        name="merchants"
                        items={selectedLOBMerchants}
                        value={selectedMerchants.current.merchants}
                        isOptionEqualToValue={(option, value) =>
                          option.merchantId === value.merchantId
                        }
                        label="Select platforms"
                        optionDisplayKey="platformName"
                        onChange={handleOnChangeMerchants}
                      />
                    )}
                  </Grid>
                )}
                {showAddMethodBtn && (
                  <Grid
                    item
                    xs={12}
                    md={4}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<RestartAlt />}
                      variant="outlined"
                      type="button"
                      onClick={resetStep1}
                      sx={{ mb: 1 }}>
                      Reset
                    </Button>
                    <Button
                      disabled={!permissions?.canEditContextualMessages}
                      fullWidth
                      size="large"
                      startIcon={<AddIcon />}
                      variant="contained"
                      type="button"
                      onClick={handleAddMerchant}>
                      Add Merchant
                    </Button>
                  </Grid>
                )}
              </Grid>
              <Typography
                className="form-header"
                variant="formHeader">
                Step 2
              </Typography>
              <Grid
                container
                rowSpacing={4}
                columnSpacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}>
                  <MultiSelect
                    id="banner"
                    name="banner"
                    items={contextualMessageBannerList}
                    label="Select Location"
                    isOptionEqualToValue={(option, value) =>
                      option.code === value.code
                    }
                    multiple={false}
                    optionDisplayKey="name"
                    customOptionLabel={(option) =>
                      `${option.name} (${option.code})`
                    }
                    customFilterStringify={(option) =>
                      `${option.name} (${option.code})`
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}>
                  <Select
                    id="bannerType"
                    name="bannerType"
                    variant="outlined"
                    required
                    label="Select Banner Type">
                    {CASHIER_BANNER_TYPES.map((type) => (
                      <MenuItem
                        key={type}
                        value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}>
                  <Field
                    name="overridePossible"
                    type="checkbox">
                    {({ input: { name, value, type, onChange } }) => (
                      <Switches
                        {...{ name, value, type, onChange }}
                        data={{
                          label: 'Allow LOB override to override this message?',
                          value,
                        }}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid
                  item
                  xs={12}>
                  <Field name="message">
                    {({ input: { name, value, onChange } }) => (
                      <TextField
                        {...{ name, value, onChange }}
                        fullWidth
                        label="Message"
                        helperText="To use translations, use the format: TRANSLATION_KEY_{actual-key}"
                        variant="outlined"
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
              {formSuccessMsg && (
                <Alert
                  onClose={() => {
                    setFormSuccessMsg('');
                  }}
                  severity="success"
                  sx={{ mt: 2 }}>
                  {formSuccessMsg}
                </Alert>
              )}
              {formErrorMsg && (
                <Alert
                  onClose={() => {
                    setFormErrorMsg('');
                  }}
                  severity="error"
                  sx={{ mt: 2 }}>
                  {formErrorMsg}
                </Alert>
              )}
              <LoadingButton
                id="submitBtn"
                fullWidth
                style={{ marginTop: '32px' }}
                size="large"
                variant="contained"
                type="submit"
                disabled={
                  pristine ||
                  submitting ||
                  !permissions?.canEditContextualMessages
                }
                loading={isSubmittingForm}
                loadingPosition="start">
                Submit
              </LoadingButton>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
